<script setup>
import {
	ref,
	computed,
	onMounted,
} from 'vue'

import { useRoute } from 'vue-router'
import { mdiCheck, mdiCar, mdiDelete, mdiClose } from '@mdi/js'
import CameraInput from '@/components/inputs/CameraInput.vue'
import DamagePosInput from '@/components/inputs/DamagePosInput.vue'
import { getImageService } from '@/services/image-service'
import { useInspectionStore, storeReady } from '@/stores/inspection-store'
import { HTTP } from '@/services/http'
import { Notify } from '@/modules/util/notify'
import { vehicleImageUrl } from '@/modules/util/vehicle-image'
import { ImageResizeManager} from '@/modules/util/imageresizer'

const store = useInspectionStore()
const imageService = getImageService(store.currentInspection)
const route = useRoute()

const props = defineProps(['field', 'modelValue'])
const emit = defineEmits(['update:modelValue'])

const show = ref(false)
const showOptions = ref([])
const namaGradeValues = ref([])

const damagePos = ref(null)
const splat = ref(props.field.splat || 'splat')
const splatOptions = ref([])
const showSplat = computed(() => props.field.zone === 'Exterior' && props.field.splat !== false)

const emptyItem = {
	damagemappos: {
		x: null,
		y: null,
	},
	date: null,
	zone: props.field.zone,
	category: null,
	component: null,
	condition: null,
	severity: null,
	notes: null,
	image: null,
	imagename: null,
}

const newItem = () => {
	const item = { ...emptyItem }
	item.date = new Date().toLocaleString('en-GB')
	return item
}

const item = ref(newItem())
const validItem = computed(() => 
	item.value.category 
	&& item.value.component 
	&& item.value.condition 
	&& item.value.severity
)

const categoryEl = ref(null)
const componentEl = ref(null)
const conditionEl = ref(null)
const severityEl = ref(null)

const showDelete = ref(false)
const selectedDamage = ref(null)

const updateDamageItems = async () => {
	show.value = false

	const items = [...(props.modelValue || [])]

	if (items.findIndex(val => {
		return val.image && val.image === item.value.image
			|| val.imagename && val.imagename === item.value.imagename
	}) === -1) {
		items.push(item.value)
		store.setDamageItems(items)
		emit('update:modelValue', items)
	}

	item.value = newItem()
}

const onImageTaken = async (value) => {
	try {
		const res = await imageService.uploadInspection(route.params.id, value.url)
		if (!res) {
			throw new Error('Image upload failed')
		}

		const blob = await ImageResizeManager.loadAndResizeImage(value.url, props.field.width || 100)
		const url = URL.createObjectURL(blob)
        
		item.value.image = url
		item.value.uid = res.uid
	} catch (e) {
		Notify.error('Unexpected error: ' + e.message)
		delete item.value.image
		return
	}
}

const showDeleteDialog = (damage) => {
	showDelete.value = true
	selectedDamage.value = damage
}

const deleteDamageItem = () => {
	const newVal = [...props.modelValue]
	const index = props.modelValue.findIndex(val => val === selectedDamage.value)

	if (index > -1) {
		const item = newVal.splice(index, 1)
		emit('update:modelValue', newVal)

		if (item.uid) {
			HTTP.deleteCached(item.uid)
		}
	}

	store.setDamageItems(newVal)

	showDelete.value = false
	selectedDamage.value = null
}

const clearItemFields = (...fields) => {
	fields.forEach(field => {
		delete item.value[field]
	})

	if (fields.includes('severity') && fields.length === 1) {
		const options = Object.keys(namaGradeValues.value[
			props.field.zone
		][ 	item.value.category
		][ 	item.value.component
		][ 	item.value.condition
		])

		if (options.length === 1) {
			item.value.severity = options[0]
		}
	}
}

const openDialog = (damage=null) => {
	item.value = damage || newItem()
	show.value = true           
}

const closeDialog = () => {
	item.value = newItem()
	show.value = false             
}

onMounted(async () => {
	await storeReady
    
	namaGradeValues.value = store.allNamaGradeValues

	if (showSplat.value) {
		splatOptions.value = damagePos.value.splats.map(option => ({
			title: option.split('-').slice(1).join(' ').toUpperCase() || 'DEFAULT',
			value: option,
		}))

		store.setNamaSplat(splat.value)
	}
})
</script>

<template>
<div>
	<div class="w-100 text-center">
		<template v-if="showSplat">
			<DamagePosInput
				ref="damagePos"
				:model-value="item.damagemappos"
				:splat-type="splat"
				:show-all="true"
				class="position-relative bg-danger mx-auto"
				style="width: clamp(300px, 100%, 600px);"
				@update:model-value="(val) => { item.damagemappos = val; show = true; }"
			/>
			<v-select
				v-model="splat"
				:items="splatOptions"
				:disabled="modelValue && !!modelValue.length"
				density="compact"
				class="w-25"
				@update:model-value="store.setNamaSplat(splat)"
			/>
		</template>
		<v-btn
			class="w-100 my-4"
			color="primary"
			@click="() => openDialog()"
		>
			{{ field.label }}
		</v-btn>
	</div>

	<v-dialog 
		v-model="show"
		fullscreen
	>
		<v-card>
			<v-toolbar>
				<v-btn
					:icon="mdiClose"
					class="ms-auto"
					variant="plain"
					@click="closeDialog"
				/>
				<v-toolbar-title>{{ field.label }}</v-toolbar-title>
			</v-toolbar>
			
			<v-card-text>
				<DamagePosInput
					v-if="showSplat"
					v-model="item.damagemappos"
					:splat-type="splat"
					class="position-relative bg-danger mx-auto"
					style="width: clamp(300px, 100%, 600px);"
				/>
				<CameraInput
					class="my-4 w-100"
					variant="outlined"
					:color="(item.image || item.imagename) ? 'success' : 'primary'"
					:append-icon="(item.image || item.imagename) ? mdiCheck : undefined"
					:disabled="(item.image || item.imagename)"
					@update="onImageTaken"
				>
					{{ (item.image || item.imagename) ? 'Picture Taken' : 'Take Picture' }}
				</CameraInput>
				<v-select
					ref="categoryEl"
					v-model="item.category"
					:items="Object.keys(namaGradeValues[field.zone])"
					label="Category"
					outlined
					dense
					clearable
					@update:model-value="clearItemFields('severity', 'condition', 'component')"
				/>
				<v-select
					v-if="item.category"
					ref="componentEl"
					v-model="item.component"
					:items="Object.keys(namaGradeValues[field.zone][item.category])"
					label="Component"
					@update:model-value="clearItemFields('severity', 'condition')"
				/>
				<v-select
					v-if="item.component"
					ref="conditionEl"
					v-model="item.condition"
					:items="Object.keys(namaGradeValues[field.zone][item.category][item.component])"
					label="Condition"
					@update:model-value="clearItemFields('severity')"
				/>
				<v-select
					v-if="item.condition"
					ref="severityEl"
					v-model="item.severity"
					:items="Object.keys(namaGradeValues[field.zone][item.category][item.component][item.condition])"
					label="Severity"
				/>
				<v-textarea
					v-model="item.repair"
					label="Notes"
					outlined
					dense
					clearable
					template
				/>
				<v-btn
					:disabled="!validItem"
					class="w-100 my-4"
					color="success"
					@click="updateDamageItems"
				>
					Save
				</v-btn>
			</v-card-text>
		</v-card>
	</v-dialog>

	<v-list>
		<v-list-item
			v-for="(damage, index) in (modelValue || []).filter(val => val.zone === field.zone)"
			:key="index"
			@click="() => {
				const current = showOptions[index]
				showOptions = []
				showOptions[index] = !current
			}"
		>
			<template #prepend>
				<v-avatar
					v-if="damage.image"
					:image="damage.image"
				/>
				<v-avatar
					v-else-if="damage.imagename"
					:image="vehicleImageUrl(damage.imagename)"
				/>
				<v-avatar
					v-else
					color="grey-lighten-1"
				>
					<v-icon color="white">
						{{ mdiCar }}
					</v-icon>
				</v-avatar>
			</template>
			<v-list-item-subtitle>
				{{ damage.date }}
			</v-list-item-subtitle>
			<v-list-item-title>
				{{ damage.category }} - {{ damage.component }} - {{ damage.condition }} - {{ damage.severity }}
			</v-list-item-title>
			<template #append>
				<v-btn
					v-show="!!showOptions[index]"
					color="success"
					size="small"
					style="margin-right: 8px"
					@click="openDialog(damage)"
				>
					Edit
				</v-btn>
				<v-btn
					v-show="!!showOptions[index]"
					color="error"
					size="small"
					:append-icon="mdiDelete"
					@click="showDeleteDialog(damage)"
				>
					Delete
				</v-btn>
			</template>
		</v-list-item>
	</v-list>

	<v-dialog v-model="showDelete">
		<v-card title="Are you sure?">  
			<v-card-text>This action cannot be undone.</v-card-text>
			<v-card-actions>
				<v-spacer />
				<v-btn @click="showDelete = false; selectedDamage = null">
					Cancel
				</v-btn>
				<v-btn
					color="primary"
					@click="deleteDamageItem"
				>
					OK
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</div>
</template>

<style lang="scss" scoped>
</style>